import React, { createContext, useContext, useMemo, useState } from 'react';

/**
 * PLAT-11877 — a place in the player's chrome for settings about what is
 * PLAYING (the Voice and Music pickers), owned by whichever panel holds that
 * state.
 *
 * The transcript panel owns the dub composition (the clip preview's bridge,
 * Creator Studio's left panel) while the player bar lives in another subtree
 * (the modal's header row, the Stage). Rather than lift the composition up
 * through the page, the bar drops a `PlayerSettingsSlot` where the pickers go
 * and the owner portals into it: the same seam as the clip preview's Languages
 * tab pane. Without a provider above it the slot renders nothing and the owner
 * finds no target, so a bar used on a surface with no dub (the review
 * workspace's player, a bare test) is unchanged.
 */
interface PlayerSettingsSlotContextValue {
  element: HTMLElement | null;
  setElement: (element: HTMLElement | null) => void;
}

const PlayerSettingsSlotContext =
  createContext<PlayerSettingsSlotContextValue | null>(null);

export const PlayerSettingsSlotProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const value = useMemo(() => ({ element, setElement }), [element]);
  return (
    <PlayerSettingsSlotContext.Provider value={value}>
      {children}
    </PlayerSettingsSlotContext.Provider>
  );
};

/** Where the player bar wants the pickers. Nothing without a provider. */
export const PlayerSettingsSlot: React.FC = () => {
  const context = useContext(PlayerSettingsSlotContext);
  if (!context) return null;
  return (
    <span
      ref={context.setElement}
      data-testid="player-settings-slot"
      style={{ display: 'contents' }}
    />
  );
};

/** The portal target for the pickers; `null` until a slot is on screen. */
export function usePlayerSettingsSlot(): HTMLElement | null {
  return useContext(PlayerSettingsSlotContext)?.element ?? null;
}
